import Backbone from '@polleverywhere/backbone'
import _ from 'underscore'
import { cleanString } from '../util'
import Images from './mixins/_images'

export default class Result extends Backbone.Model {
  /**
   * Getter for the result source.
   * @return {string}
   */
  get source () {
    return this.options.source
  }

  /**
   * Update the result source.
   * @return {string}
   */
  set source (value) {
    this.options.source = value
  }

  /**
   * @param {Object} attrs
   * @param {Object} options
   * @param {Poll} options.poll Poll model that this result pertains to.
   * @param {Poll} options.source Human ID where result originated from.
   *    https://ci.ops.pe/browse/RA-REDOC/latest/artifact/JOB1/Site/ci.html#tag/Participant::Results
   */
  initialize (attrs, options = {}) {
    _.extend(this, Images(this))

    this.options = _.defaults({}, options, {
      poll: null,
      source: null
    })
  }

  urlRoot () {
    return this.poll() ? this.poll().instance.resultUrl : undefined
  }

  /**
   * Seralized data to be sent to the respective endpoint on `save`,
   * @return {Object}
   */
  toJSON () {
    const json = this.attributes

    // Add source key if it exists.
    if (this.source) {
      json.source = this.source
    }

    return json
  }

  /**
   * TODO(shared): Consider moving this into a property named `activity` b/c
   * results are not just tied to polls but surveys also. The name is
   * misleading and doesn't need to be a function. When doing this, make sure to
   * update all consumers to use the new property instead.
   *
   * Get the poll associated with this result. If the poll reference to this
   * model doesn't exist, try getting it from the collection's poll.
   * @param {newPoll} newPoll If optionally provided, updates poll reference.
   * @return {Backbone.Model|null}
   */
  poll (newPoll) {
    if (typeof newPoll !== 'undefined') {
      this.options.poll = newPoll
    }
    const collectionPoll = this.collection ? this.collection.poll : null
    return this.options.poll || collectionPoll
  }

  isArchived () {
    return this.get('archive_id') || false
  }

  approve () {
    return this.save({ approved: true }, { patch: true })
  }

  reject () {
    return this.save({ approved: false }, { patch: true })
  }

  isApproved () {
    return this.get('approved') === true
  }

  isRejected () {
    return this.get('approved') === false
  }

  isShownCorrect () {
    const pollIsGraded = this.collection.poll.get('grading_enabled') && this.collection.poll.regions.hasCorrect()

    // if a collection does not exist just return result model correctness
    // if a collection does exist, check both the regions and the result model for correctness
    const resultIsCorrect = !this.collection ? this.get('correct') : (this.get('correct') || this.collection.poll.regions.isValueCorrect(this.get('value')))
    return pollIsGraded && resultIsCorrect
  }

  isUnmoderated () {
    return this.get('approved') === null
  }

  isDeleted () {
    return !!this.get('deleted_at')
  }

  cleanValue () {
    return cleanString(this.get('value'))
  }

  moderationState () {
    switch (this.get('approved')) {
      case true:
        return 'approved'
      case false:
        return 'rejected'
      default:
        return 'pending'
    }
  }

  shouldBeDisplayed () {
    if (this.isRejected() || this.isDeleted()) {
      return false
    }

    const moderationEnabled = this.poll() && this.poll().get('moderation_enabled')
    if (moderationEnabled && this.isUnmoderated()) {
      return false
    }

    return true
  }
}
